import { Table } from 'antd';
import { BarChart3, SearchX, ShieldAlert } from 'lucide-react';
import type { OffcardReportGroup } from '@chukua/common';
import { Navigate } from 'react-router';
import { ListSearch } from '../../../../../shared/components/ListSearch';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { PageHeader } from '../../../../../shared/components/PageHeader';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { withinSection } from '../../../../../shared/components/section-trail';
import { buildOffcardColumns } from '../components/build-offcard-columns';
import { useOffcardReport } from '../hooks/use-offcard-report';

/** Where the rate card has no price for work being done (PRD → Rate cards → Off-card jobs; gitaumoses4/chukua#35): jobs priced on and off the card, by customer, lane and month. */
export function OffcardReportPage() {
  const { state, list, isRefreshing } = useOffcardReport();

  if (state.status === 'forbidden') return <Navigate to="/rate-cards" replace />;

  const empty = list.isFiltered ? (
    <EmptyState icon={SearchX} title="No matching jobs" description="No priced jobs match these filters." />
  ) : (
    <EmptyState icon={BarChart3} title="Nothing to report yet" description="Jobs priced on or off the rate card appear here, by customer, lane and month." />
  );

  return (
    <>
      <PageHeader
        breadcrumbs={withinSection('rate-cards')}
        title="Off-card report"
        description="Jobs priced on and off the rate card. A lane marked with no price on the card is one the card is missing."
      />
      {state.status === 'error' && (
        <EmptyState
          icon={ShieldAlert}
          title="Couldn't load the report"
          description="Something went wrong loading the off-card report. Try reloading the page."
        />
      )}
      {state.status === 'loading' && <TableSkeleton label="Loading off-card report" withSearch />}
      {state.status === 'loaded' && (
        <>
          <ListSearch value={list.query.q} placeholder="Search customer, zone or job type" onSearch={(q) => list.setQuery({ q }, { replace: true })} />
          <Table
            rowKey={(group) =>
              [group.customerId, group.originZoneCode, group.destinationZoneCode, group.jobTypeCode, group.rateBasis, group.period, group.currency].join('|')
            }
            dataSource={state.page.items}
            columns={buildOffcardColumns({ list })}
            loading={isRefreshing}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: empty }}
            {...list.tableProps<OffcardReportGroup>(state.page.total)}
          />
        </>
      )}
    </>
  );
}
